<template>
  <div class="stmp-chat-widget h-100">
    <div class="chat-header d-flex align-items-center justify-content-between pb-2">
      <strong>Wiadomości nt. ogłoszenia #{{ offerId }}</strong>
      <span v-if="connected" class="p-1 rounded bg-success"></span>
      <span v-else class="bg-secondary"></span>
    </div>
    <div v-if="blocked" class="alert alert-warning mb-3">
      {{ blockedReason || 'To ogłoszenie nie jest już aktywne. Czat został zablokowany.' }}
    </div>
    <div class="chat-body h-[550px]" ref="bodyRef">
      <div v-for="m in messages" :key="m.id || m._localId" class="chat-message"
           :class="{me: m.sender_ps_customer_id === mePsId}">
        <div class="meta">
          <small>
            <span v-if="m.sender_ps_customer_id === sellerPsId">Sprzedawca</span>
            <span v-else-if="m.sender_ps_customer_id === buyerPsId">Kupujący</span>
            <span v-else>Użytkownik</span>
            • {{ formatDate(m.created_at) }}
          </small>
        </div>
        <div class="bubble">
          {{ m.content }}
        </div>
      </div>
    </div>
    <form class="chat-input d-flex gap-2" @submit.prevent="onSend">
      <input type="text" v-model="draft" class="form-control" :placeholder="placeholder" :disabled="blocked"/>
      <button class="btn btn-primary" :disabled="!canSend">Wyślij</button>
    </form>
  </div>
</template>

<script setup lang="ts">
import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {useApiConfig, postJson} from '../composables/useApi';

interface MessageDto {
  id?: number;
  _localId?: string;
  offer_id: number;
  seller_ps_customer_id: number;
  buyer_ps_customer_id: number;
  sender_ps_customer_id: number;
  content: string;
  created_at: string;
}

const props = defineProps<{
  offerId: number;
  buyerId?: number;
}>();

const cfg = useApiConfig();
const messages = ref<MessageDto[]>([]);
const draft = ref('');
const connected = ref(false);
const bodyRef = ref<HTMLElement | null>(null);
const blocked = ref(false);
const blockedReason = ref<string>('');

const state = ref<{
  sellerPsId: number;
  buyerPsId: number;
  mePsId: number;
  role: 'seller' | 'buyer' | 'unknown'
}>({sellerPsId: 0, buyerPsId: 0, mePsId: 0, role: 'unknown'});
const sellerPsId = computed(() => state.value.sellerPsId);
const buyerPsId = computed(() => state.value.buyerPsId);
const mePsId = computed(() => state.value.mePsId);

let ws: WebSocket | null = null;

const placeholder = computed(() => blocked.value ? 'Czat zablokowany' : 'Napisz wiadomość…');
const canSend = computed(() => !blocked.value && draft.value.trim().length > 0 && state.value.sellerPsId > 0 && state.value.buyerPsId > 0);

function scrollToBottom() {
  nextTick(() => {
    const el = bodyRef.value;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  });
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function normalizeMessage(m: any): MessageDto {
  return {
    ...m,
    id: m.id != null ? Number(m.id) : undefined,
    offer_id: Number(m.offer_id),
    seller_ps_customer_id: Number(m.seller_ps_customer_id),
    buyer_ps_customer_id: Number(m.buyer_ps_customer_id),
    sender_ps_customer_id: Number(m.sender_ps_customer_id),
  } as MessageDto;
}

async function initConversation() {
  if (!cfg.apiChatInit) return;
  try {
    const res = await postJson<any>(cfg.apiChatInit, {offerId: props.offerId});
    if (!res?.success) return;
  const d = res.data;
  state.value = {
    sellerPsId: Number(d.sellerPsCustomerId),
    buyerPsId: Number(d.buyerPsCustomerId),
    mePsId: Number(d.mePsCustomerId),
    role: (d.role || 'unknown')
  };
  if (state.value.role === 'seller' && props.buyerId && props.buyerId > 0) {
    state.value.buyerPsId = Number(props.buyerId);
  }
  blocked.value = false;
  blockedReason.value = '';
  } catch (e: any) {
    blocked.value = true;
    blockedReason.value = 'To ogłoszenie nie jest już aktywne. Czat został zablokowany.';
  }
}

async function loadHistory() {
  if (!cfg.apiChatHistory) return;
  try {
    const res = await postJson<any>(cfg.apiChatHistory, {
      offerId: props.offerId,
      buyerPsCustomerId: state.value.buyerPsId,
    });
    if (res?.success) {
    const list = (res.data.messages || []).map(normalizeMessage);
    messages.value = list;
    if (list.length > 0) {
      const first = list[0];
      if (!Number.isFinite(state.value.sellerPsId) || state.value.sellerPsId <= 0) {
        state.value.sellerPsId = Number(first.seller_ps_customer_id);
      }
      if (!Number.isFinite(state.value.buyerPsId) || state.value.buyerPsId <= 0) {
        state.value.buyerPsId = Number(first.buyer_ps_customer_id);
      }
    }
    scrollToBottom();
    }
  } catch (e:any) {
    blocked.value = true;
    if (!blockedReason.value) blockedReason.value = 'To ogłoszenie nie jest już aktywne. Czat został zablokowany.';
  }
}

function openSocket() {
  if (!cfg.wsChatUrl) return;
  const url = new URL(cfg.wsChatUrl);
  url.searchParams.set('offerId', String(props.offerId));
  url.searchParams.set('seller', String(state.value.sellerPsId));
  url.searchParams.set('buyer', String(state.value.buyerPsId));
  url.searchParams.set('me', String(state.value.mePsId));
  ws = new WebSocket(url.toString());
  ws.onopen = () => {
    connected.value = true;
  };
  ws.onclose = () => {
    connected.value = false;
  };
  ws.onerror = () => {
    connected.value = false;
  };
  ws.onmessage = (ev) => {
    try {
      const payload = JSON.parse(ev.data);
      if (payload?.type === 'message' && payload.data) {
        const nm = normalizeMessage(payload.data);
        if (!Number.isFinite(state.value.sellerPsId) || state.value.sellerPsId <= 0) {
          state.value.sellerPsId = Number(nm.seller_ps_customer_id);
        }
        if (!Number.isFinite(state.value.buyerPsId) || state.value.buyerPsId <= 0) {
          state.value.buyerPsId = Number(nm.buyer_ps_customer_id);
        }
        messages.value.push(nm);
        scrollToBottom();
      }
    } catch {
    }
  };
}

async function onSend() {
  const text = draft.value.trim();
  if (!text || !cfg.apiChatSend) return;
  const res = await postJson<any>(cfg.apiChatSend, {
    offerId: props.offerId,
    content: text,
    buyerPsCustomerId: state.value.buyerPsId,
  });
  if (res?.success) {
    const m = normalizeMessage(res.data.message);
    messages.value.push(m);
    draft.value = '';
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({type: 'message', data: m}));
    }
    scrollToBottom();
  }
}

onMounted(async () => {
  await initConversation();
  await loadHistory();
  openSocket();
});

onBeforeUnmount(() => {
  try {
    ws?.close();
  } catch {
  }
});

watch(() => props.offerId, async () => {
  try {
    ws?.close();
  } catch {
  }
  messages.value = [];
  await initConversation();
  await loadHistory();
  openSocket();
});

watch(() => props.buyerId, async () => {
  try {
    ws?.close();
  } catch {
  }
  messages.value = [];
  await initConversation();
  await loadHistory();
  openSocket();
});
</script>
